import type { ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Bars, Card, Chip, DataTable, ProvenanceTag } from "@/ui";
import { events } from "@/mock";
import { fmt, formatDelta } from "@/lib/format";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useEvents } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { useSignalsQuery, matches } from "../searchContext";
import { AigenTag, EmptyState, SampleChip, Skeleton, panelState } from "../wiring";

const KIND_LABEL: Record<string, string> = {
  pageview: "pageview",
  autocapture: "auto",
  custom: "custom",
};

/** A normalized event row both sample + live produce. */
interface ViewEvent {
  name: string;
  count: number;
  kind: string;
  value?: number; // total_value (live only)
  delta?: number; // sample only
  lastSeen?: string; // sample only
}

export function Events() {
  const { siteId, range, live } = useWorkspace();
  const eventsQ = useEvents(siteId, range);

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: eventsQ.isLoading,
    isError: eventsQ.isError,
    hasData: !!eventsQ.data,
  });

  const isLive = state === "live";

  const items: ViewEvent[] =
    isLive && eventsQ.data?.items
      ? eventsQ.data.items.map((e) => ({
          name: e.name || e.action || e.category,
          count: e.count,
          kind: e.category || "custom",
          value: e.total_value,
        }))
      : events.map((e) => ({
          name: e.name,
          count: e.count,
          kind: e.kind,
          delta: e.delta,
          lastSeen: e.lastSeen,
        }));

  const q = useSignalsQuery();
  const shown = items.filter((e) => matches(q, e.name, e.kind));
  const empty = isLive && (!eventsQ.data || eventsQ.data.items.length === 0);
  const noMatch = !!q && shown.length === 0 && items.length > 0;
  const topEvents = [...shown].sort((a, b) => b.count - a.count).slice(0, 5);

  const provenance = isLive ? (
    <AigenTag />
  ) : (
    <span className="flex items-center gap-2">
      <ProvenanceTag source="posthog" />
      <SampleChip />
    </span>
  );

  // Columns differ slightly: live exposes total value, sample exposes Δ/last-seen.
  const rows: Record<string, ReactNode>[] = shown.map((e) => ({
    event: (
      <span className="flex items-center gap-2">
        <span className="font-mono text-[12px] text-ink-strong">{e.name}</span>
        <Chip mono>{KIND_LABEL[e.kind] ?? e.kind}</Chip>
      </span>
    ),
    count: fmt(e.count),
    ...(isLive
      ? { value: e.value ? fmt(Math.round(e.value)) : "—" }
      : {
          delta: (
            <span
              className={
                "inline-flex items-center justify-end gap-1 font-mono text-[12px] " +
                ((e.delta ?? 0) >= 0 ? "text-muted" : "text-accent")
              }
            >
              {(e.delta ?? 0) >= 0 ? (
                <TrendingUp className="h-3 w-3" strokeWidth={2} />
              ) : (
                <TrendingDown className="h-3 w-3" strokeWidth={2} />
              )}
              {formatDelta(e.delta ?? 0)}
            </span>
          ),
          lastSeen: e.lastSeen,
        }),
  }));

  const columns = isLive
    ? [
        { key: "event", label: "Event" },
        { key: "count", label: "Count", align: "right" as const, mono: true },
        { key: "value", label: "Total value", align: "right" as const, mono: true },
      ]
    : [
        { key: "event", label: "Event" },
        { key: "count", label: "Count", align: "right" as const, mono: true },
        { key: "delta", label: "Δ", align: "right" as const },
        { key: "lastSeen", label: "Last seen", align: "right" as const, mono: true },
      ];

  if (state === "loading") {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[220px]" />
        <Skeleton className="h-[320px]" />
      </div>
    );
  }

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Events"
        desc="Every tracked action, from autocaptured clicks to custom conversions."
        action={provenance}
      />

      {empty ? (
        <EmptyState
          title="No events yet"
          detail="Tracked actions show up here as your visitors interact."
        />
      ) : noMatch ? (
        <EmptyState title={`No events match “${q}”`} detail="Try a different search term." />
      ) : (
        <>
          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
                Top events
              </h3>
              {provenance}
            </div>
            <Bars data={topEvents.map((e) => ({ label: e.name, value: e.count }))} accentTop />
          </Card>

          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
                All events
              </h3>
              {provenance}
            </div>
            <DataTable columns={columns} rows={rows} />
          </Card>
        </>
      )}
    </div>
  );
}
